/**
 * Featured lists: one compact table per city.
 */
import React, { useMemo, useState } from "react";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/shared/page-header";
import { SearchField } from "@/components/shared/search-field";
import { CITY_OPTIONS } from "@/constants/cities";
import type { ListResponse } from "@/types/lists.types";

import { useFeaturedLists } from "../lists/hooks/use-lists";
import { groupListsByCity } from "./group-by-city";
import { FeaturedCityTable } from "./featured-city-table";

const FeaturedListsPage: React.FC = () => {
  const { data: listsResponse, isLoading, error } = useFeaturedLists();
  const [city, setCity] = useState("all");
  const [query, setQuery] = useState("");

  const lists = (listsResponse?.data || []) as ListResponse[];

  const filtered = useMemo(() => {
    let results = city === "all" ? lists : lists.filter((l) => l.city === city);
    const q = query.trim().toLowerCase();
    if (q) results = results.filter((l) => l.listName.toLowerCase().includes(q) || l.creatorName?.toLowerCase().includes(q));
    return results;
  }, [lists, city, query]);

  const grouped = useMemo(() => groupListsByCity(filtered), [filtered]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Featured"
        emphasis="lists"
        description="Lists other members have chosen to share."
        stats={[{ value: lists.length, label: lists.length === 1 ? "list" : "lists" }]}
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchField id="featured-search" value={query} onChange={setQuery} placeholder="Search lists or people" />
        <Select value={city} onValueChange={setCity}>
          <SelectTrigger className="h-8 w-[160px] text-[13px]" aria-label="City">
            <SelectValue placeholder="All cities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {CITY_OPTIONS.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error ? (
        <p className="text-[13.5px] text-muted-foreground">Could not load featured lists. Reload the page to try again.</p>
      ) : isLoading ? (
        <p className="text-[13.5px] text-muted-foreground">Loading…</p>
      ) : grouped.length === 0 ? (
        <p className="text-[13.5px] text-muted-foreground">
          {query.trim() ? "No lists match your search." : "No featured lists yet."}
        </p>
      ) : (
        <div className="flex flex-col gap-8">
          {grouped.map(([cityKey, cityLists]) => (
            <FeaturedCityTable key={cityKey} cityKey={cityKey} lists={cityLists} />
          ))}
        </div>
      )}
    </div>
  );
};

export default FeaturedListsPage;
